import { useEffect } from "react";
import { applyJointValue } from "../kinematics";
import { usePhysicsStore } from "../../stores/physicsStore";
import { getSceneActions, getSceneState } from "../../stores/sceneStore";
import { useViewerStore } from "../../stores/viewerStore";
import { setupIkTargetMarkerBridge } from "../ik/ikTargetMarker";
import { setupCameraProbeMarkerBridge } from "../cameraProbeMarker";
import { EMPTY_OBJECT } from "../scene/sceneConstants";
import { safeTraverse } from "../scene/sceneObjectLookups";
import {
  getJointCssLabelMainText,
  indexActuatorsForJointVisualization,
} from "../jointActuatorLabelUtils";
import { setJointLabelDisplayedName } from "../labelUtils";
import { logger } from "../../utils/logger";

export function useJointKinematicsEvents({
  findJointVizGroup,
  getJointSnapshot,
  isJointUpdatingRef,
  isPhysicsRunning,
  jointEntries,
  jointLookup,
  jointObjectsRef,
  sceneObjectsRef,
  sceneRef,
  slideJointLimitControlsRef,
  updateJointVizPivotFromRef,
}) {
  // Updates joint arc visualizations (range indicators) when joint limits change
  useEffect(() => {
    if (!sceneRef.current) return;

    // Update all joint visualizations when joint state changes
    jointEntries.forEach((joint) => {
      if (!joint?.uuid) return;
      const jointIdentifier = joint.uuid;
      const jointData = getJointSnapshot(jointIdentifier);
      if (!jointData) return;

      const min = jointData.min;
      const max = jointData.max;

      safeTraverse(sceneRef.current, (obj) => {
        if (
          obj.userData &&
          obj.userData.isJointVizGroup &&
          obj.userData.jointUuid === jointIdentifier
        ) {
          if (obj.userData.updateAll) {
            obj.userData.updateAll(
              min,
              max,
              jointData.axis,
              jointData.useLimits,
              slideJointLimitControlsRef.current[jointIdentifier] || null,
              jointData.current
            );
          } else {
            if (
              obj.userData.arc &&
              obj.userData.arc.userData &&
              obj.userData.arc.userData.updateArc
            ) {
              obj.userData.arc.userData.updateArc(min, max);
            }
            if (
              obj.userData.minSphere &&
              obj.userData.minSphere.userData &&
              obj.userData.minSphere.userData.updatePosition
            ) {
              obj.userData.minSphere.userData.updatePosition(min);
            }
            if (
              obj.userData.maxSphere &&
              obj.userData.maxSphere.userData &&
              obj.userData.maxSphere.userData.updatePosition
            ) {
              obj.userData.maxSphere.userData.updatePosition(max);
            }
          }
        }
      });
    });
  }, [getJointSnapshot, jointEntries, jointLookup, sceneRef, slideJointLimitControlsRef]);

  // Refresh joint baselines immediately when a body transform is committed
  useEffect(() => {
    const onBodyTransform = (e) => {
      try {
        // UUID-only lookup (critical for duplicate robots)
        const bodyUuid = e?.detail?.bodyUuid;
        const sceneObj = bodyUuid ? sceneObjectsRef.current?.[bodyUuid] : null;

        if (!sceneObj) return;

        // Get position and quaternion from event detail, or fall back to store lookup
        let position = e?.detail?.position;
        let quaternion = e?.detail?.quaternion;

        // Fallback: if not in event detail, look up from store
        if ((!position || !quaternion) && bodyUuid) {
          const sceneActions = getSceneActions();
          if (bodyUuid) {
            position = sceneActions.getBodyPosition(bodyUuid);
          }
          if (bodyUuid) {
            quaternion = sceneActions.getBodyRotation(bodyUuid);
          }
        }

        // Update the scene object's position and rotation immediately
        if (position && Array.isArray(position) && position.length === 3) {
          sceneObj.position.set(position[0], position[1], position[2]);
        }

        if (quaternion && Array.isArray(quaternion) && quaternion.length === 4) {
          // Quaternion format: [w, x, y, z] -> Three.js expects (x, y, z, w)
          sceneObj.quaternion.set(quaternion[1], quaternion[2], quaternion[3], quaternion[0]);
        }

        // Update matrix to apply changes immediately
        sceneObj.updateMatrixWorld(true);

        const isDescendantOf = (child, ancestor) => {
          let p = child;
          while (p) {
            if (p === ancestor) return true;
            p = p.parent;
          }
          return false;
        };

        // Update the moved body and all descendants that host joints
        Object.values(jointObjectsRef.current || {}).forEach((entry) => {
          const { bodyGroup } = entry || {};
          if (!bodyGroup) return;
          if (bodyGroup === sceneObj || isDescendantOf(bodyGroup, sceneObj)) {
            entry.initialPos = bodyGroup.position.clone();
            entry.initialRot = bodyGroup.quaternion.clone();
          }
        });
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
    };

    window.addEventListener("body-transform-updated", onBodyTransform);
    return () => window.removeEventListener("body-transform-updated", onBodyTransform);
  }, [jointObjectsRef, sceneObjectsRef]);

  // Updates joint visualizations and reapplies kinematics when joint axes change
  useEffect(() => {
    if (!sceneRef.current) return;
    isJointUpdatingRef.current = true;

    try {
      // Update all joint objects with current axis data and reapply kinematics
      jointEntries.forEach((joint) => {
        if (!joint?.uuid) return;
        const jointIdentifier = joint.uuid;
        const jointData = getJointSnapshot(jointIdentifier);
        if (!jointData) return;

        const jointRef =
          (jointObjectsRef.current && jointObjectsRef.current[jointIdentifier]) || null;

        // Sync axis and range from the snapshot into jointObjectsRef so the viz update below
        // always uses current store values, not stale initial values.
        if (jointRef && Array.isArray(jointData.axis) && jointData.axis.length === 3) {
          jointRef.joint.axis = [...jointData.axis];
        }
        if (jointRef && Number.isFinite(jointData.min) && Number.isFinite(jointData.max)) {
          jointRef.joint.range = [jointData.min, jointData.max];
        }
        if (jointRef && jointData.useLimits) {
          jointRef.joint.useLimits = jointData.useLimits;
        }

        // Update the visualization
        safeTraverse(sceneRef.current, (obj) => {
          if (
            obj.userData &&
            obj.userData.isJointVizGroup &&
            obj.userData.jointUuid === jointIdentifier
          ) {
            // Prefer range from joint object, fallback to derived snapshot min/max, then sensible defaults
            const ref = jointRef;
            const range =
              Array.isArray(ref?.joint?.range) && ref.joint.range.length === 2
                ? ref.joint.range
                : Number.isFinite(jointData.min) && Number.isFinite(jointData.max)
                  ? [jointData.min, jointData.max]
                  : String(ref?.joint?.kind || "").toLowerCase() === "slide"
                    ? [0, 1]
                    : [-Math.PI, Math.PI];
            const min = range[0];
            const max = range[1];

            // Always update arc/endpoints visualization on axis/range change
            if (obj.userData.updateAll) {
              obj.userData.updateAll(
                min,
                max,
                jointData.axis,
                jointData.useLimits,
                slideJointLimitControlsRef.current[jointIdentifier] || null,
                jointData.current
              );
            } else if (
              obj.userData.arc &&
              obj.userData.arc.userData &&
              obj.userData.arc.userData.updateArc
            ) {
              obj.userData.arc.userData.updateArc(min, max);
            }
          }
        });

        // Re-apply the joint value to update the kinematic chain
        // Skip when physics is running - physics will drive the visualization via applyLatestPoses
        if (jointRef && !isPhysicsRunning) {
          const { bodyGroup, joint, initialRot, initialPos, parentRot } = jointRef;

          applyJointValue(bodyGroup, joint, jointData.current, initialRot, initialPos, parentRot);
        }
      });
    } finally {
      isJointUpdatingRef.current = false;
    }
  }, [
    getJointSnapshot,
    isJointUpdatingRef,
    isPhysicsRunning,
    jointEntries,
    jointLookup,
    jointObjectsRef,
    sceneRef,
    slideJointLimitControlsRef,
  ]);

  // NEW: live update of joint pivot (pos) when UI/store changes it
  useEffect(() => {
    const posHandler = (e) => {
      isJointUpdatingRef.current = true;
      try {
        const { jointUuid, pos } = e.detail || {};
        const identifier = jointUuid;
        if (!identifier || !Array.isArray(pos)) return;
        const ref = (jointObjectsRef.current && jointObjectsRef.current[identifier]) || null;
        if (!ref) return;
        ref.joint.position = pos; // update pivot offset
        // Move the joint visualization group to match new local pivot position
        try {
          const viz = findJointVizGroup(identifier);
          updateJointVizPivotFromRef(viz, ref, pos);
        } catch (error) {
          logger.warn("[Scene] Unexpected error during scene update", error);
        }
        const { bodyGroup, joint, initialRot, initialPos, parentRot } = ref;
        const js = getJointSnapshot(identifier) || { current: 0 };
        const currentVal = js.current ?? 0;
        applyJointValue(bodyGroup, joint, currentVal, initialRot, initialPos, parentRot);
      } finally {
        isJointUpdatingRef.current = false;
      }
    };

    const axisHandler = (e) => {
      const { jointUuid, axis } = e.detail || {};
      const identifier = jointUuid;
      if (!identifier || !Array.isArray(axis)) return;
      try {
        const viz = findJointVizGroup(identifier);
        if (viz && Array.isArray(axis) && axis.length === 3) {
          // Preserve the group's q=0 body transform; only the local arc geometry changes.
          if (viz.userData.updateAll) {
            const jointState = getSceneState().joints[identifier];
            const range =
              Array.isArray(jointState?.range) && jointState.range.length === 2
                ? jointState.range
                : [-Math.PI, Math.PI];
            viz.userData.updateAll(
              range[0],
              range[1],
              axis,
              jointState?.useLimits ?? "auto",
              slideJointLimitControlsRef.current[identifier] || null,
              jointState?.ref ?? 0
            );
          }
        }
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
    };

    const nameHandler = (e) => {
      const { jointUuid, name } = e.detail || {};
      if (!jointUuid || typeof name !== "string") return;
      try {
        const viz = findJointVizGroup(jointUuid);
        if (viz) {
          viz.userData.jointName = name;
        }
        if (viz?.userData?.siteLabel?.element) {
          const showActuators = useViewerStore.getState().showActuators;
          const state = getSceneState();
          const { actuatorDisplayLabelByJointUuid } = indexActuatorsForJointVisualization(
            state.actuators || EMPTY_OBJECT,
            state.joints || EMPTY_OBJECT
          );
          const label = getJointCssLabelMainText({
            showActuators,
            jointName: name,
            jointUuid,
            actuatorDisplayLabelByJointUuid,
          });
          setJointLabelDisplayedName(viz.userData.siteLabel.element, label);
        }
        const ref = jointObjectsRef.current?.[jointUuid];
        if (ref) {
          ref.jointName = name;
          if (ref.joint) ref.joint.name = name;
        }
      } catch (error) {
        logger.warn("[Scene] Unexpected error during joint name update", error);
      }
    };

    window.addEventListener("joint-pos-updated", posHandler);
    window.addEventListener("joint-axis-updated", axisHandler);
    window.addEventListener("joint-name-updated", nameHandler);

    // Handle joint value updates (including from equality constraints)
    // Skip when physics is running - physics will drive the visualization
    const valueHandler = (e) => {
      if (usePhysicsStore.getState().isRunning) return;
      const { jointUuid, value } = e.detail || {};
      const identifier = jointUuid;
      if (!identifier || value === undefined) return;
      const ref = (jointObjectsRef.current && jointObjectsRef.current[identifier]) || null;
      if (!ref) return;
      const { bodyGroup, joint, initialRot, initialPos, parentRot } = ref;
      applyJointValue(bodyGroup, joint, value, initialRot, initialPos, parentRot);
      if (joint?.kind === "slide") {
        const viz = findJointVizGroup(identifier);
        if (viz?.userData?.updateCurrent) {
          viz.userData.updateCurrent(value);
        }
      }
    };

    const stopIkMarker = setupIkTargetMarkerBridge(sceneRef);
    const stopCameraProbeMarker = setupCameraProbeMarkerBridge(sceneRef);

    window.addEventListener("joint-value-updated", valueHandler);

    return () => {
      window.removeEventListener("joint-pos-updated", posHandler);
      window.removeEventListener("joint-axis-updated", axisHandler);
      window.removeEventListener("joint-name-updated", nameHandler);
      window.removeEventListener("joint-value-updated", valueHandler);
      if (typeof stopIkMarker === "function") stopIkMarker();
      if (typeof stopCameraProbeMarker === "function") stopCameraProbeMarker();
      isJointUpdatingRef.current = false;
    };
  }, [
    findJointVizGroup,
    getJointSnapshot,
    isJointUpdatingRef,
    jointLookup,
    jointObjectsRef,
    sceneRef,
    slideJointLimitControlsRef,
    updateJointVizPivotFromRef,
  ]);
}
